import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Person,
  Logout,
  Dashboard,
  History,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '../../store/authStore';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from '../../store/languageStore';
import { colors, brand } from '../../theme';
import { useIsInsideShell } from '../../contexts/ShellContext';
import beoLogo from '../../assets/images/beo-logo.png';

const AppHeader: React.FC = () => {
  // All hooks must be called unconditionally before any early return.
  const isInsideShell = useIsInsideShell();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (isInsideShell) return null;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
    handleMenuClose();
  };

  return (
    <>
      <AppBar position="sticky" elevation={0} sx={{ backgroundColor: brand.yellow, color: brand.textPrimary, borderBottom: `1px solid ${brand.borderSubtle}`, top: 0, zIndex: 1100 }}>
        <Toolbar sx={{ minHeight: { xs: 44, sm: 48 }, px: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, flexGrow: 1 }}>
            {/* Logo */}
            <Box
              component="img"
              src={beoLogo}
              alt="BEO Software"
              width={136}
              height={65}
              onClick={() => navigate(user?.is_admin ? '/admin/dashboard' : '/process')}
              sx={{
                height: { xs: 28, sm: 32 },
                width: 'auto',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  filter: 'brightness(1.1)',
                }
              }}
            />
            {/* App Name */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                cursor: 'pointer',
                color: brand.textPrimary,
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                display: { xs: 'none', sm: 'block' },
                '&:hover': {
                  opacity: 0.9,
                }
              }}
              onClick={() => navigate(user?.is_admin ? '/admin/dashboard' : '/dashboard')}
            >
              {t('common.appName')}
            </Typography>
            {/* Plan Badge */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.25,
                borderRadius: 20,
                background: 'rgba(255, 255, 255, 0.95)',
                color: user?.selected_plan === 'enterprise' ? '#2e7d32' : user?.selected_plan === 'advanced' ? '#f57c00' : colors.primary,
                fontSize: '0.65rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                },
              }}
              onClick={() => navigate('/profile')}
            >
              {user?.selected_plan === 'enterprise' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: '#4caf50',
                      boxShadow: '0 0 4px rgba(76, 175, 80, 0.6)',
                    }}
                  />
                  {t('plans.enterprise')}
                </Box>
              ) : user?.selected_plan === 'advanced' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: '#ff9800',
                      boxShadow: '0 0 4px rgba(255, 152, 0, 0.6)',
                    }}
                  />
                  {t('plans.advanced')}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: colors.primary,
                      boxShadow: `0 0 4px ${colors.primary}99`,
                    }}
                  />
                  {t('plans.essential')}
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LanguageSelector size="small" sx={{ minWidth: 100, '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' } }} />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: brand.textPrimary,
                fontSize: '0.8rem',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {user?.firstname} {user?.lastname}
            </Typography>
            <IconButton
              onClick={handleMenuOpen}
              sx={{
                p: 0.25,
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  bgcolor: 'transparent',
                }
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: brand.orange,
                  color: '#fff',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    filter: 'brightness(1.08)',
                  }
                }}
              >
                {user?.firstname?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { navigate(user?.is_admin ? '/admin/dashboard' : '/dashboard'); handleMenuClose(); }}>
          <Dashboard sx={{ mr: 1, fontSize: 20 }} />
          {t('navigation.dashboard')}
        </MenuItem>
        <MenuItem onClick={() => { navigate('/profile'); handleMenuClose(); }}>
          <Person sx={{ mr: 1, fontSize: 20 }} />
          {t('navigation.profile')}
        </MenuItem>
        <MenuItem onClick={() => { navigate('/transactions'); handleMenuClose(); }}>
          <History sx={{ mr: 1, fontSize: 20 }} />
          {t('dashboard.cards.history.title')}
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <Logout sx={{ mr: 1, fontSize: 20 }} />
          {t('auth.logout')}
        </MenuItem>
      </Menu>
    </>
  );
};

export default AppHeader;
